"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "UsersService", {
    enumerable: true,
    get: function() {
        return UsersService;
    }
});
const _util = require("util");
const _common = require("@nestjs/common");
const _nocodbsdk = require("nocodb-sdk");
const _uuid = require("uuid");
const _isEmail = /*#__PURE__*/ _interop_require_default(require("validator/lib/isEmail"));
const _bcryptjs = /*#__PURE__*/ _interop_require_default(require("bcryptjs"));
const _verifyDefaultWorkspace = require("~/helpers/verifyDefaultWorkspace");
const _utils = require("~/utils");
const _helpers = require("~/services/users/helpers");
const _apphooksservice = require("~/services/app-hooks/app-hooks.service");
const _helpers1 = require("~/helpers");
const _metaservice = require("~/meta/meta.service");
const _globals = require("~/utils/globals");
const _Noco = /*#__PURE__*/ _interop_require_default(require("~/Noco"));
const _models = require("~/models");
const _stringHelpers = require("~/helpers/stringHelpers");
const _catchError = require("~/helpers/catchError");
const _basesservice = require("~/services/bases.service");
const _extractProps = require("~/helpers/extractProps");
const _deepClone = /*#__PURE__*/ _interop_require_default(require("~/helpers/deepClone"));
const _mailservice = require("~/services/mail/mail.service");
const _Mail = require("~/interface/Mail");
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let UsersService = class UsersService {
    // allow signup/signin only if email matches against pattern
    validateEmailPattern(email) {
        const emailPattern = process.env.NC_AUTH_EMAIL_PATTERN;
        if (emailPattern) {
            const regex = new RegExp(emailPattern);
            if (!regex.test(email)) {
                _catchError.NcError.forbidden('Not allowed to signup/signin with this email');
            }
        }
    }
    async findOne(_email) {
        const user = await _models.User.getByEmail(_email);
        await _models.PresignedUrl.signMetaIconImage(user);
        return user;
    }
    async insert(param, ncMeta = this.metaService || _Noco.default.ncMeta) {
        var _param_email;
        return ncMeta.metaInsert2(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.USERS, _extends({}, param, {
            email: (_param_email = param.email) == null ? void 0 : _param_email.toLowerCase()
        }));
    }
    async profileUpdate({ id, params, req }) {
        const oldUser = await _models.User.get(id);
        const updateObj = (0, _extractProps.extractProps)(params, [
            'display_name',
            'avatar',
            'is_new_user',
            'meta'
        ]);
        const user = await _models.User.update(id, updateObj);
        this.appHooksService.emit(_nocodbsdk.AppEvents.USER_PROFILE_UPDATE, {
            user: (0, _deepClone.default)(user),
            oldUser,
            req
        });
        await _models.PresignedUrl.signMetaIconImage(user);
        return user;
    }
    async registerNewUserIfAllowed({ email, salt, password, email_verification_token, req, is_invite = false }, ncMeta = _Noco.default.ncMeta) {
        this.validateEmailPattern(email);
        let roles = _nocodbsdk.OrgUserRoles.CREATOR;
        const isFirstUser = await _models.User.isFirst(ncMeta);
        if (isFirstUser && process.env.NC_CLOUD !== 'true') {
            roles = `${_nocodbsdk.OrgUserRoles.CREATOR},${_nocodbsdk.OrgUserRoles.SUPER_ADMIN}`;
            // todo: update in nc_store
            // roles = 'owner,creator,editor'
            _utils.T.emit('evt', {
                evt_type: 'base:invite',
                count: 1
            });
        } else {
            const settings = await _Noco.default.getAppSettings();
            if ((settings == null ? void 0 : settings.invite_only_signup) && !is_invite) {
                _catchError.NcError.badRequest('Not allowed to signup, contact super admin.');
            } else {
                roles = _nocodbsdk.OrgUserRoles.VIEWER;
            }
        }
        const token_version = (0, _stringHelpers.randomTokenString)();
        const user = await _models.User.insert({
            email,
            salt,
            password,
            email_verification_token,
            roles,
            token_version
        }, ncMeta);
        // if first user and super admin, create a base
        if (isFirstUser && !_utils.isEE) {
            await (0, _verifyDefaultWorkspace.verifyDefaultWorkspace)(user, ncMeta);
            // todo: update swagger type
            user.createdProject = await this.createDefaultProject(user, req, ncMeta);
        }
        // todo: update swagger type
        return user;
    }
    async passwordChange(param) {
        (0, _helpers1.validatePayload)('swagger.json#/components/schemas/PasswordChangeReq', param.body);
        const { currentPassword, newPassword } = param.body;
        if (!currentPassword || !newPassword) {
            return _catchError.NcError.badRequest('Missing new/old password');
        }
        // validate password and throw error if password is satisfying the conditions
        const { valid, error } = (0, _nocodbsdk.validatePassword)(newPassword);
        if (!valid) {
            _catchError.NcError.badRequest(`Password : ${error}`);
        }
        const user = await _models.User.getByEmail(param.user.email);
        const hashedPassword = await (0, _util.promisify)(_bcryptjs.default.hash)(currentPassword, user.salt);
        if (hashedPassword !== user.password) {
            return _catchError.NcError.badRequest('Current password is wrong');
        }
        const salt = await (0, _util.promisify)(_bcryptjs.default.genSalt)(10);
        const password = await (0, _util.promisify)(_bcryptjs.default.hash)(newPassword, salt);
        await _models.User.update(user.id, {
            salt,
            password,
            email: user.email,
            token_version: (0, _stringHelpers.randomTokenString)()
        });
        // delete all refresh token and populate a new one
        await _models.UserRefreshToken.deleteAllUserToken(user.id);
        this.appHooksService.emit(_nocodbsdk.AppEvents.USER_PASSWORD_CHANGE, {
            user: user,
            req: param.req
        });
        return true;
    }
    async passwordForgot(param) {
        (0, _helpers1.validatePayload)('swagger.json#/components/schemas/PasswordForgotReq', param.body);
        const _email = param.body.email;
        if (!_email) {
            _catchError.NcError.badRequest('Please enter your email address.');
        }
        const email = _email.toLowerCase();
        const user = await _models.User.getByEmail(email);
        if (user) {
            const token = (0, _uuid.v4)();
            const updatedUser = await _models.User.update(user.id, {
                email: user.email,
                reset_password_token: token,
                reset_password_expires: new Date(Date.now() + 60 * 60 * 1000),
                token_version: (0, _stringHelpers.randomTokenString)()
            });
            try {
                await this.mailService.sendMail({
                    mailEvent: _Mail.MailEvent.RESET_PASSWORD,
                    payload: {
                        user: updatedUser,
                        req: param.req
                    }
                });
            } catch (e) {
                return _catchError.NcError.badRequest('Email Plugin is not found. Please contact administrators to configure it in App Store first.');
            }
            this.appHooksService.emit(_nocodbsdk.AppEvents.USER_PASSWORD_FORGOT, {
                user: user,
                req: param.req
            });
        }
        return true;
    }
    async tokenValidate(param) {
        const token = param.token;
        const user = await _Noco.default.ncMeta.metaGet(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.USERS, {
            reset_password_token: token
        });
        if (!user || !user.email) {
            _catchError.NcError.badRequest('Invalid reset url');
        }
        if (new Date(user.reset_password_expires) < new Date()) {
            _catchError.NcError.badRequest('Password reset url expired');
        }
        return true;
    }
    async passwordReset(param) {
        (0, _helpers1.validatePayload)('swagger.json#/components/schemas/PasswordResetReq', param.body);
        const { token, body } = param;
        const user = await _Noco.default.ncMeta.metaGet(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.USERS, {
            reset_password_token: token
        });
        if (!user) {
            _catchError.NcError.badRequest('Invalid reset url');
        }
        if (user.reset_password_expires < new Date()) {
            _catchError.NcError.badRequest('Password reset url expired');
        }
        if (user.provider && user.provider !== 'local') {
            _catchError.NcError.badRequest('Email registered via social account');
        }
        // validate password and throw error if password is satisfying the conditions
        const { valid, error } = (0, _nocodbsdk.validatePassword)(body.password);
        if (!valid) {
            _catchError.NcError.badRequest(`Password : ${error}`);
        }
        const salt = await (0, _util.promisify)(_bcryptjs.default.genSalt)(10);
        const password = await (0, _util.promisify)(_bcryptjs.default.hash)(body.password, salt);
        await _models.User.update(user.id, {
            salt,
            password,
            email: user.email,
            reset_password_expires: null,
            reset_password_token: '',
            token_version: (0, _stringHelpers.randomTokenString)()
        });
        // delete all refresh tokens to invalidate existing sessions
        await _models.UserRefreshToken.deleteAllUserToken(user.id);
        this.appHooksService.emit(_nocodbsdk.AppEvents.USER_PASSWORD_RESET, {
            user: user,
            req: param.req
        });
        return true;
    }
    async emailVerification(param) {
        const { token, req } = param;
        const user = await _Noco.default.ncMeta.metaGet(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.USERS, {
            email_verification_token: token
        });
        if (!user) {
            _catchError.NcError.badRequest('Invalid verification url');
        }
        await _models.User.update(user.id, {
            email: user.email,
            email_verification_token: '',
            email_verified: true
        });
        this.appHooksService.emit(_nocodbsdk.AppEvents.USER_EMAIL_VERIFICATION, {
            user: user,
            req
        });
        return true;
    }
    async refreshToken(param) {
        try {
            var _param_req_cookies, _param_req;
            if (!((_param_req = param.req) == null ? void 0 : (_param_req_cookies = _param_req.cookies) == null ? void 0 : _param_req_cookies.refresh_token)) {
                _catchError.NcError.badRequest(`Missing refresh token`);
            }
            const oldRefreshToken = param.req.cookies.refresh_token;
            const userRefreshToken = await _models.UserRefreshToken.getByToken(oldRefreshToken);
            if (!userRefreshToken) {
                _catchError.NcError.unauthorized(`Invalid refresh token`);
            }
            // check if refresh token expired and delete it if expired
            if (userRefreshToken.expires_at && new Date(userRefreshToken.expires_at) < new Date()) {
                await _models.UserRefreshToken.deleteToken(oldRefreshToken);
                _catchError.NcError.unauthorized(`Refresh token expired`);
            }
            const user = await _models.User.get(userRefreshToken.fk_user_id);
            if (!user) {
                _catchError.NcError.unauthorized(`Invalid refresh token`);
            }
            const refreshToken = (0, _stringHelpers.randomTokenString)();
            try {
                await _models.UserRefreshToken.updateOldToken(oldRefreshToken, refreshToken);
            } catch (error) {
                console.error('Failed to update old refresh token:', error);
                _catchError.NcError.internalServerError('Failed to update refresh token');
            }
            (0, _helpers.setTokenCookie)(param.res, refreshToken);
            return {
                token: (0, _helpers.genJwt)(_extends({}, user, {
                    extra: userRefreshToken.meta
                }), _Noco.default.getConfig())
            };
        } catch (e) {
            _catchError.NcError.badRequest(e.message);
        }
    }
    async signup(param) {
        (0, _helpers1.validatePayload)('swagger.json#/components/schemas/SignUpReq', param.body);
        const { email: _email, token, ignore_subscribe } = param.req.body;
        let { password } = param.req.body;
        // validate password and throw error if password is satisfying the conditions
        const { valid, error } = (0, _nocodbsdk.validatePassword)(password);
        if (!valid) {
            _catchError.NcError.badRequest(`Password : ${error}`);
        }
        if (!(0, _isEmail.default)(_email)) {
            _catchError.NcError.badRequest(`Invalid email`);
        }
        // Reject plus addressing (always abusive)
        if (_email.split('@')[0].includes('+')) {
            _catchError.NcError.badRequest('Email aliases with "+" are not allowed');
        }
        const email = _email.toLowerCase();
        this.validateEmailPattern(email);
        // Check for existing user by canonical email to prevent alias abuse
        let user = await _models.User.getByCanonicalEmail(email) || await _models.User.getByEmail(email);
        if (user) {
            if (token) {
                if (token !== user.invite_token) {
                    _catchError.NcError.badRequest(`Invalid invite url`);
                } else if (user.invite_token_expires < new Date()) {
                    _catchError.NcError.badRequest('Expired invite url, Please contact super admin to get a new invite url');
                }
            } else {
            // todo : opening up signup for timebeing
            // return next(new Error(`Email '${email}' already registered`));
            }
        }
        const salt = await (0, _util.promisify)(_bcryptjs.default.genSalt)(10);
        password = await (0, _util.promisify)(_bcryptjs.default.hash)(password, salt);
        const email_verification_token = (0, _uuid.v4)();
        if (!ignore_subscribe) {
            _utils.T.emit('evt_subscribe', email);
        }
        let createdProject = undefined;
        if (user) {
            if (token) {
                await _models.User.update(user.id, {
                    salt,
                    password,
                    email_verification_token,
                    invite_token: null,
                    invite_token_expires: null,
                    email: user.email
                });
            } else {
                _catchError.NcError.badRequest('User already exist');
            }
        } else {
            const { createdProject: _createdProject } = await this.registerNewUserIfAllowed({
                email,
                salt,
                password,
                email_verification_token,
                req: param.req
            });
            createdProject = _createdProject;
        }
        user = await _models.User.getByEmail(email);
        // TODO: Right now we are not actively enforcing email verification @pranavxc
        // so we are not sending email verification email
        // but we should send email verification email
        // once we start enforcing email
        /* try {
      await this.mailService.sendMail({
        mailEvent: MailEvent.VERIFY_EMAIL,
        payload: {
          user,
          req: param.req,
        },
      });
    } catch (e) {
      this.logger.warn(
        'Warning : `mailSend` failed, Please configure emailClient configuration.',
      );
    }
*/ const refreshToken = (0, _stringHelpers.randomTokenString)();
        await _models.UserRefreshToken.insert({
            token: refreshToken,
            fk_user_id: user.id
        });
        (0, _helpers.setTokenCookie)(param.res, refreshToken);
        this.appHooksService.emit(_nocodbsdk.AppEvents.USER_SIGNUP, {
            user: user,
            req: param.req
        });
        await this.mailService.sendMail({
            mailEvent: _Mail.MailEvent.WELCOME,
            payload: {
                user,
                req: param.req
            }
        });
        this.appHooksService.emit(_nocodbsdk.AppEvents.WELCOME, {
            user,
            req: param.req
        });
        return _extends({}, await this.login(user, param.req), {
            createdProject
        });
    }
    async login(user, req) {
        this.appHooksService.emit(_nocodbsdk.AppEvents.USER_SIGNIN, {
            user,
            req
        });
        return {
            token: (0, _helpers.genJwt)(user, _Noco.default.getConfig())
        };
    }
    async signOut(param) {
        try {
            this.clearCookie(param);
            const user = param.req.user;
            if (user == null ? void 0 : user.id) {
                await _models.User.update(user.id, {
                    token_version: (0, _stringHelpers.randomTokenString)()
                });
                // todo: clear only token present in cookie to avoid invalidating all refresh token
                await _models.UserRefreshToken.deleteAllUserToken(user.id);
            }
            return {
                msg: 'Signed out successfully'
            };
        } catch (e) {
            _catchError.NcError.badRequest(e.message);
        }
    }
    clearCookie(param) {
        param.res.clearCookie('refresh_token');
    }
    async createDefaultProject(user, req, ncMeta = _Noco.default.ncMeta) {
        // create new base for user
        const base = await this.basesService.createDefaultBase({
            user,
            req
        }, ncMeta);
        return base;
    }
    async setRefreshToken({ res, req }) {
        var _req_user, _req_user1;
        const userId = (_req_user = req.user) == null ? void 0 : _req_user.id;
        if (!userId) return;
        const user = await _models.User.get(userId);
        if (!user) return;
        const refreshToken = (0, _stringHelpers.randomTokenString)();
        if (!user['token_version']) {
            user['token_version'] = (0, _stringHelpers.randomTokenString)();
            await _models.User.update(user.id, {
                token_version: user['token_version']
            });
        }
        await _models.UserRefreshToken.insert({
            token: refreshToken,
            fk_user_id: user.id,
            meta: (_req_user1 = req.user) == null ? void 0 : _req_user1.extra
        });
        (0, _helpers.setTokenCookie)(res, refreshToken);
    }
    constructor(metaService, appHooksService, basesService, mailService){
        this.metaService = metaService;
        this.appHooksService = appHooksService;
        this.basesService = basesService;
        this.mailService = mailService;
        this.logger = new _common.Logger(UsersService.name);
    }
};
UsersService = _ts_decorate([
    (0, _common.Injectable)(),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof _metaservice.MetaService === "undefined" ? Object : _metaservice.MetaService,
        typeof _apphooksservice.AppHooksService === "undefined" ? Object : _apphooksservice.AppHooksService,
        typeof _basesservice.BasesService === "undefined" ? Object : _basesservice.BasesService,
        typeof _mailservice.MailService === "undefined" ? Object : _mailservice.MailService
    ])
], UsersService);
